import { sanitizeUrl } from "@braintree/sanitize-url";
import Box from "@mui/material/Box";
import CanSendTagContext from "components/CanSendTagContext";
import LanguageContext from "components/LanguageContext";
import PageVersionContext from "components/PageVersionContext";
import PropTypes from "prop-types";
import React, { forwardRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import scrollIntoView from "scroll-into-view-if-needed";
import EulerianService from "services/EulerianService";
import languages from "utils/languagesTypes";

export const componentClassName = "Da-Link";

const internalNavigationUrls = (process.env.internal_navigation_urls || "").split(",");

const RouterLink = forwardRef((props, ref) => <HashLink innerRef={ref} {...props} />);

const Link = forwardRef((props, ref) => {
  const {
    url = "",
    page = null,
    external = false,
    title = "",
    iconPosition = "right",
    size = "md",
    iconDSFR = "",
    button = false,
    basic = false,
    disabled = false,
    children = null,
    onClick = () => null,
    className = "",
    sx = { cursor: disabled ? "not-allowed" : "pointer" },
    anchor = "",
    ...others
  } = props;

  const { language } = useContext(LanguageContext);
  const canSendTag = useContext(CanSendTagContext);
  const { setCurrentPageVersion } = useContext(PageVersionContext);
  const { pathname, search } = useLocation();

  if (!url && !page && !anchor) {
    return (
      <Box
        component="a"
        onClick={(e) => {
          onClick(e);
          e.preventDefault();
        }}
        className={`${componentClassName} ${className}`}
        sx={sx}
        {...others}
      >
        {children}
      </Box>
    );
  }

  if (anchor) {
    return (
      <Box component="a" href={`#${anchor}`} className={`${componentClassName} ${className}`} sx={sx} {...others}>
        {children}
      </Box>
    );
  }

  const sendTag = () => {
    if (canSendTag) {
      let name = title || (page && page.title) || (page && page.tab && page.tab.title) || children;

      if (Array.isArray(name)) {
        name.forEach((element) => {
          if (typeof element === "string") {
            name = element;
          }
        });
      }

      if (typeof name === "string") {
        EulerianService.sendClick(
          button
            ? { button: [`button::click::${name.toLowerCase()}`] }
            : { link: [`link::click::${name.toLowerCase()}`] },
        );
      }
    }
  };

  const handleClickLink = (e) => {
    if (!disabled) {
      sendTag();
      if (typeof onClick === "function") {
        onClick(e);
      }
      scrollIntoView(document.querySelector("header"));
      if (page && !external) {
        setCurrentPageVersion(page);
      }
    }
  };

  const dynamicProps = {
    component: RouterLink,
    target: external ? "_blank" : "_self",
    rel: external ? "noopener noreferrer" : undefined,
  };

  const getProtocol = (oldUrl) =>
    oldUrl && (oldUrl.match(/^(http|https):\/\//g) || oldUrl.startsWith("/")) ? oldUrl : `https://`.concat(oldUrl);
  let path = null;
  if (page) {
    path = page?.fullPath || "/";
  }
  if (url) {
    path = sanitizeUrl(getProtocol(url));
  }

  if (path) {
    if (external || !path.startsWith("/")) {
      dynamicProps.href =
        !internalNavigationUrls.some((str) => url?.indexOf(str) !== -1) && !url?.includes("lang=")
          ? `${path}${path.indexOf("?") !== -1 ? "&" : "?"}lang=${languages[language]?.lang}`
          : path;
      dynamicProps.component = "a";
    } else {
      const [nextPathname, ...params] = path.split(/(\?|#)/i);
      let nextSearch = "";
      let nextHash = "";
      if (params && params.length > 1) {
        if (params.indexOf("?") !== -1) {
          nextSearch = `?${params[params.indexOf("?") + 1]}`;
        }
        if (params.indexOf("#") !== -1) {
          nextHash = `#${params[params.indexOf("#") + 1]}`;
        }
      }

      dynamicProps.to = {
        pathname: nextPathname,
        search: nextSearch,
        hash: nextHash,
        state: { prevPath: pathname + search },
      };
    }
  }

  if (disabled) {
    dynamicProps.href = null;
  }
  const titleLink = title || (typeof children === "string" ? children : null);

  // W3C: remove attribute "to" in span component
  if (typeof document !== "undefined") {
    const seeMoreLink = document.getElementById("spanLink");
    if (seeMoreLink) {
      seeMoreLink.removeAttribute("to");
    }
  }

  let linkClassName = "";
  if (!basic) {
    linkClassName = "fr-link";
    if (size) {
      linkClassName += ` fr-link--${size}`;
    }
  }
  if (iconDSFR && iconPosition) {
    linkClassName += ` fr-link--icon-${iconPosition}`;
  }
  if (!external && iconDSFR) {
    linkClassName += ` fr-icon-${iconDSFR}`;
  }

  return (
    <Box
      component="a"
      ref={ref}
      onClick={handleClickLink}
      title={titleLink}
      {...dynamicProps}
      className={`${className} ${linkClassName}`}
      sx={{ cursor: disabled ? "inherit" : "pointer", ...sx }}
      {...others}
    >
      {children}
    </Box>
  );
});

Link.propTypes = {
  url: PropTypes.string,
  page: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
  title: PropTypes.string,
  onClick: PropTypes.func,
  external: PropTypes.bool,
  iconPosition: PropTypes.string,
  size: PropTypes.string,
  basic: PropTypes.bool,
  iconDSFR: PropTypes.string,
  button: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  anchor: PropTypes.string,
  sx: PropTypes.shape(),
};

export default Link;
